import React, { useState } from "react";
import { find, get, filter, split, join, map, capitalize } from "lodash";
import { NavLink } from "react-router-dom";
import { CSSTransition } from "react-transition-group";
import classNames from "classnames";
import { Ripple } from "primereact/ripple";
import { Badge } from "primereact/badge";
import { Tooltip } from "primereact/tooltip";
import { InputText } from "primereact/inputtext";
import useAuth from "hooks/useAuth";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as SolidIcons from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from 'react-i18next';
const AppSubmenu = (props) => {
  const [activeIndex, setActiveIndex] = useState(null);
  const auth = useAuth();
  const hypenToCapCase = (val) => {
    const arr = split(val, "-");
    const updatedArr = map(arr, (str) => capitalize(str));
    return join(updatedArr, "");
  };

  const onMenuItemClick = (event, item, index) => {
    //avoid processing disabled items
    if (item.disabled) {
      event.preventDefault();
      return true;
    }

    //execute command
    if (item.command) {
      item.command({ originalEvent: event, item: item });
    }

    if (index === activeIndex) setActiveIndex(null);
    else setActiveIndex(index);

    if (props.onMenuItemClick) {
      props.onMenuItemClick({
        originalEvent: event,
        item: item,
      });
    }
  };

  const onKeyDown = (event) => {
    if (event.code === "Enter" || event.code === "Space") {
      event.preventDefault();
      event.target.click();
    }
  };

  function itemVisible(item) {
    const visible =
      typeof item.visible === "function"
        ? item.visible()
        : item.visible !== false;
    return visible;
  }

  const renderLinkContent = (item) => {
    let submenuIcon = item.items && (
      <i className="pi pi-fw pi-angle-down menuitem-toggle-icon"></i>
    );
    let badge = item.badge && <Badge value={item.badge} />;
    const tooltip = <Tooltip target=".menu-icon" />;
    let menuIcon =
      item.icon &&
      (item.isModule ? (
        item.icon.includes("/") ? (
          <img
            className={`menu-icon ${item.childNavbar ? "parent" : "child"}`}
            src={item.icon}
            style={{
              paddingLeft: "10px",
              paddingRight: "15px",
              width: "43px",
            }}
            data-pr-tooltip={item.label}
            data-pr-position="right"
            data-pr-at="right+5 top"
          />
        ) : (
          <FontAwesomeIcon
            className={`menu-icon ${item.childNavbar ? "parent" : "child"}`}
            style={{
              paddingLeft: "10px",
              paddingRight: "15px",
            }}
            icon={SolidIcons["fa" + hypenToCapCase(item.icon)]}
            data-pr-tooltip={item.label}
            data-pr-position="right"
            data-pr-at="right+5 top"
          />
        )
      ) : (
        <i
          className={`menu-icon pi pi-${item.icon} ${
            item.childNavbar ? "parent" : "child"
          }`}
          style={{
            paddingLeft: "10px",
            paddingRight: "15px",
          }}
          data-pr-tooltip={item.label}
          data-pr-position="right"
          data-pr-at="right+5 top"
        />
      ));
    let menuLabel = item.label && (
      <span className="menu-label">{item.label}</span>
    );
    return (
      <React.Fragment>
        {tooltip}
        {menuIcon}
        {menuLabel}
        {submenuIcon}
        {badge}
        <Ripple />
      </React.Fragment>
    );
  };

  const renderLink = (item, i) => {
    let content = renderLinkContent(item);
    if (!itemVisible(item)) {
      return null;
    }
    if (item.to) {
      return (
        <NavLink
          className={({ isActive }) =>
            isActive
              ? "p-ripple router-link-active router-link-exact-active flex"
              : "p-ripple flex"
          }
          aria-label={item.label}
          onKeyDown={onKeyDown}
          role="menuitem"
          to={item.to}
          onClick={(e) => onMenuItemClick(e, item, i)}
          exact="true"
          target={item.target}
          style={{
            height: "60px",
            borderLeft: `7px solid #${item.color}`,
          }}
        >
          {content}
        </NavLink>
      );
    } else {
      return (
        <a
          tabIndex="0"
          aria-label={item.label}
          onKeyDown={onKeyDown}
          role="menuitem"
          href={item.url}
          className="p-ripple"
          onClick={(e) => onMenuItemClick(e, item, i)}
          target={item.target}
        >
          {content}
        </a>
      );
    }
  };

  let items =
    props.items &&
    props.items.map((item, i) => {
      let active = activeIndex === i;
      let styleClass = classNames(item.badgeStyleClass, {
        "layout-menuitem-category": props.root,
        "active-menuitem": active && !item.to,
      });

      if (
        (item.rbac && item.rbac.indexOf(auth.user.roleName) !== -1) ||
        !item.rbac
      ) {
        if (props.root) {
          return (
            <li className={styleClass} key={i} role="none">
              {props.root === true && (
                <React.Fragment>
                  <div
                    className="layout-menuitem-root-text"
                    aria-label={item.label}
                  >
                    {item.label}
                  </div>
                  <AppSubmenu
                    items={item.items}
                    onMenuItemClick={props.onMenuItemClick}
                  />
                </React.Fragment>
              )}
            </li>
          );
        } else {
          return (
            <li className={styleClass} key={i} role="none">
              {renderLink(item, i)}
              <CSSTransition
                classNames="layout-submenu-wrapper"
                timeout={{ enter: 200, exit: 450 }}
                in={active}
                unmountOnExit
              >
                <AppSubmenu
                  items={item.items}
                  onMenuItemClick={props.onMenuItemClick}
                />
              </CSSTransition>
            </li>
          );
        }
      }
    });

  return items ? (
    <ul className={props.className} role="menu">
      {items}
    </ul>
  ) : null;
};

const getAppSubMenu = (props, items, isOverflow) => {
  return (
    <div
      className="layout-menu-container"
      style={{
        overflowY: isOverflow ? "auto" : "",
      }}
    >
      <AppSubmenu
        items={items}
        className="layout-menu"
        onMenuItemClick={props.onMenuItemClick}
        role="menu"
      />
    </div>
  );
};

export const AppMenu = (props) => {
  const auth = useAuth();
  const isParent = find(get(props, "model"), (item) =>
    get(item, "childNavbar")
  );
  if (isParent) {
    return (
      <React.Fragment>
        {getAppSubMenu(
          props,
          filter(
            get(props, "model"),
            (item) => get(item, "childNavbar", []).length > 0
          )
        )}
        {getAppSubMenu(
          props,
          filter(
            get(props, "model"),
            (item) => get(item, "childNavbar", []).length === 0
          )
        )}
      </React.Fragment>
    );
  } else {
    const notModules = filter(
      get(props, "model"),
      (item) => !get(item, "isModule", false)
    );
    let modules = filter(
      get(props, "model"),
      (item) => get(item, "isModule", false),
      []
    );
    if (["Admin", "User"].includes(auth?.user?.roleName)) {
      modules = filter(
        get(props, "model"),
        (item) => get(item, "isModule", false) && auth?.user?.modules_ids.includes(item.moduleId),
        []
      )
    }
    
    const [searchText, setSearchText] = useState("");
    const {t}=useTranslation();
    return (t,
      <React.Fragment>
        {getAppSubMenu(props, notModules)}
        {modules.length > 0 ? (
          <InputText
            type="text"
            placeholder={t("SEARCH_FOR_MODULES")}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        ) : null}
        {getAppSubMenu(
          props,
          filter(modules, (module) =>
            module.label?.toLowerCase().includes(searchText.toLowerCase())
          ),
          true
        )}
      </React.Fragment>
    );
  }
};
